import type { ScanStatus } from '@myrewards/contracts';
import type { ReactElement } from 'react';
import { cx, Icon, type IconName } from '../ui';
import { initialsOf, SCAN_WORDS } from './words';
import s from './Shared.module.css';

// APP-148: the pieces the step, Waiting on others and the Approved confirmation draw alike.

/** A picture (a pasted screenshot) or any other file, by what it is, not by its name. */
export function fileIconName(contentType: string): IconName {
  return contentType.startsWith('image/') ? 'image' : 'file';
}

export function FileIcon({ contentType }: { contentType: string }): ReactElement {
  return (
    <span className={s.fileIcon} aria-hidden="true">
      <Icon name={fileIconName(contentType)} size={20} />
    </span>
  );
}

/** Initials in a circle; the Stock Admin's in the info ink. */
export function Avatar({ name, actor = false }: { name: string; actor?: boolean }): ReactElement {
  return (
    <span className={cx(s.avatar, actor && s.avatarActor)} aria-hidden="true">
      {initialsOf(name)}
    </span>
  );
}

/** Both people, the one who acted first: "Recorded as" and "Done by". */
export function Pair({ actor, person }: { actor: string; person: string }): ReactElement {
  return (
    <span className={s.pair} aria-hidden="true">
      <Avatar name={actor} actor />
      <Avatar name={person} />
    </span>
  );
}

/** The virus check in words, with its mark ("Checking", "Checked"). */
export function ScanWords({
  status,
  label,
}: {
  status: ScanStatus;
  /** Words of the caller's own (an upload still on its way), in the waiting tone. */
  label?: string;
}): ReactElement {
  const scan = label === undefined ? SCAN_WORDS[status] : { label, tone: 'waiting' as const };
  return (
    <span
      className={cx(
        s.scan,
        scan.tone === 'done' && s.scanDone,
        scan.tone === 'waiting' && s.scanWaiting,
        scan.tone === 'problem' && s.scanProblem,
      )}
    >
      <Icon
        name={scan.tone === 'done' ? 'check' : scan.tone === 'problem' ? 'alert' : 'clock'}
        size={14}
      />
      {scan.label}
    </span>
  );
}

export { s as shared };
